<template>
    <div class="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
        <div class="p-4 border-b border-gray-200 bg-gray-50">
            <h3 class="text-lg font-semibold text-gray-800">수급 상세</h3>
            <p v-if="selectedInfo" class="text-xs text-gray-500 mt-1">
                {{ selectedInfo }}
            </p>
            <p v-else class="text-xs text-gray-500 mt-1">
                수급 현황 테이블에서 셀을 클릭하면 상세 내역을 확인할 수 있습니다.
            </p>
        </div>
        <div class="overflow-x-auto">
            <table v-if="groupedRecords.length > 0" class="w-full text-sm table-fixed">
                <colgroup>
                    <col style="width: 80px;">
                    <col style="width: 110px;">
                    <col style="width: auto; min-width: 180px;">
                    <col style="width: 110px;">
                    <col style="width: 180px;">
                    <col style="width: 180px;">
                </colgroup>
                <thead class="bg-gray-50 border-b sticky top-0">
                    <tr>
                        <th class="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                            시장
                        </th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                            만기
                        </th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                            종목명
                        </th>
                        <th class="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                            체결금리
                        </th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                            매도기관(거래액)
                        </th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                            매수기관(거래액)
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="group in groupedRecords" :key="group.code" class="hover:bg-gray-50 transition-colors">
                        <td class="px-4 py-3 text-sm text-center">
                            <div class="flex flex-col gap-1">
                                <span v-for="market in group.markets" :key="market"
                                    :class="getMarketBadgeClass(market)">
                                    {{ getMarketLabel(market) }}
                                </span>
                            </div>
                        </td>
                        <td class="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                            {{ formatMaturityDate(group.maturityDate) }}
                        </td>
                        <td class="px-4 py-3 text-sm text-gray-900 truncate" :title="group.bondName">
                            {{ group.bondName }}
                        </td>
                        <td class="px-4 py-3 text-sm text-center text-gray-900 whitespace-nowrap">
                            {{ formatYield(group.yield) }}
                        </td>
                        <td class="px-4 py-3 text-sm">
                            <div class="flex flex-col gap-1">
                                <div v-for="(seller, index) in group.sellers" :key="index" class="text-gray-900"
                                    :class="{ 'font-bold': isSelectedInstitution(seller.institutionKey) }">
                                    {{ seller.institution }} ({{ seller.amount }}억)
                                </div>
                                <span v-if="group.sellers.length === 0" class="text-gray-300">-</span>
                            </div>
                        </td>
                        <td class="px-4 py-3 text-sm">
                            <div class="flex flex-col gap-1">
                                <div v-for="(buyer, index) in group.buyers" :key="index" class="text-gray-900"
                                    :class="{ 'font-bold': isSelectedInstitution(buyer.institutionKey) }">
                                    {{ buyer.institution }} ({{ buyer.amount }}억)
                                </div>
                                <span v-if="group.buyers.length === 0" class="text-gray-300">-</span>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div v-else class="p-8 text-center text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto mb-3 text-gray-400" fill="none"
                    viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p class="text-sm">선택된 데이터가 없습니다</p>
            </div>
        </div>
    </div>
</template>

<script>
import { computed } from 'vue';
import {
    institutionTypeLabel,
    creditTypeLabel,
    maturityBucketLabel,
    toEok,
    GroupType,
} from '@/utils/marketFlowUtil';

export default {
    name: 'DetailTable',
    props: {
        records: {
            type: Array,
            required: true,
        },
        selectedCell: {
            type: Object,
            default: () => ({ rowKey: null, colKey: null }),
        },
        groupType: {
            type: String,
            required: true,
        },
        directionFilter: {
            type: Object,
            required: true,
        },
        selectedInstitution: {
            type: String,
            default: null,
        },
    },
    setup(props) {
        const selectedInfo = computed(() => {
            const { rowKey, colKey } = props.selectedCell;
            if (!rowKey || !colKey) return null;

            const rowLabel =
                props.groupType === GroupType.BOND_TYPE
                    ? institutionTypeLabel[rowKey]
                    : creditTypeLabel[rowKey];
            const colLabel = maturityBucketLabel[colKey];

            const directionLabels = [];
            if (props.directionFilter.buy) directionLabels.push('매수');
            if (props.directionFilter.sell) directionLabels.push('매도');
            if (props.directionFilter.net) directionLabels.push('순매수');

            return `${rowLabel} · ${colLabel} · ${directionLabels.join('/')} 기준`;
        });

        // code별로 레코드 그룹화
        const groupedRecords = computed(() => {
            const groups = new Map();

            for (const record of props.records) {
                const key = record.code;

                if (!groups.has(key)) {
                    groups.set(key, {
                        code: record.code,
                        bondName: record.bondName,
                        maturityDate: record.maturityDate,
                        yield: record.representationTradingYield,
                        markets: new Set(),
                        sellers: [],
                        buyers: [],
                    });
                }

                const group = groups.get(key);
                group.markets.add(record.marketType);

                if (record.flowType === 'OUT') {
                    group.sellers.push({
                        institution: institutionTypeLabel[record.institutionType] || record.institutionType,
                        institutionKey: record.institutionType,
                        amount: Math.round(toEok(record.amount)),
                    });
                } else if (record.flowType === 'IN') {
                    group.buyers.push({
                        institution: institutionTypeLabel[record.institutionType] || record.institutionType,
                        institutionKey: record.institutionType,
                        amount: Math.round(toEok(record.amount)),
                    });
                }
            }

            // Set을 Array로 변환하고 만기순으로 정렬 (만기 짧은 순)
            return Array.from(groups.values())
                .map(group => ({
                    ...group,
                    markets: Array.from(group.markets),
                }))
                .sort((a, b) => {
                    // maturityDate를 Date 객체로 변환하여 비교
                    const dateA = new Date(a.maturityDate);
                    const dateB = new Date(b.maturityDate);
                    return dateA - dateB; // 오름차순 (만기 짧은 순)
                });
        });

        const formatMaturityDate = dateStr => {
            // YYYY-MM-DD 형식을 YYYY.MM.DD로 변환
            return dateStr.replace(/-/g, '.');
        };

        const formatYield = yieldValue => {
            if (!yieldValue) return '-';
            return `${Number(yieldValue).toFixed(3)}%`;
        };

        const formatAmountEok = amount => {
            return toEok(amount).toFixed(1);
        };

        const getInstitutionLabel = institutionType => {
            return institutionTypeLabel[institutionType] || institutionType;
        };

        const getMarketLabel = marketType => {
            return marketType === 'PRIMARY' ? '발행' : '유통';
        };

        const getMarketBadgeClass = marketType => {
            const baseClass = 'inline-block px-2 py-0.5 text-xs font-medium rounded';
            if (marketType === 'PRIMARY') {
                return `${baseClass} bg-indigo-100 text-indigo-700`;
            }
            return `${baseClass} bg-slate-100 text-slate-700`;
        };

        // 선택된 기관인지 확인
        const isSelectedInstitution = institutionKey => {
            if (props.groupType === GroupType.BOND_TYPE) {
                // 섹터별 분류: rowKey가 institutionType (은행, 보험 등)
                const { rowKey } = props.selectedCell;
                return rowKey === institutionKey;
            } else {
                // 기관별 분류: 섹션1에서 선택된 기관과 비교
                // rowKey는 creditType (공사채, 은행채 등)이므로 사용하지 않음
                return props.selectedInstitution === institutionKey;
            }
        };

        return {
            selectedInfo,
            groupedRecords,
            formatMaturityDate,
            formatYield,
            formatAmountEok,
            getInstitutionLabel,
            getMarketLabel,
            getMarketBadgeClass,
            isSelectedInstitution,
        };
    },
};
</script>

<style scoped>
.scrollbar-always-visible {
    scrollbar-gutter: stable;
}

.scrollbar-always-visible::-webkit-scrollbar {
    width: 8px;
}

.scrollbar-always-visible::-webkit-scrollbar-track {
    background: #f1f1f1;
}

.scrollbar-always-visible::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
}

.scrollbar-always-visible::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}
</style>
